package com.macrofocus.eflux.lod

import com.macrofocus.eflux.network.QuestDBWeightedNetwork
import com.macrofocus.eflux.network.physical.Branch
import com.macrofocus.eflux.network.physical.Bus
import com.macrofocus.eflux.ui.ChordTemplate
import com.macrofocus.visflux.chord.Arc
import com.macrofocus.visflux.chord.ChordView
import com.macrofocus.visflux.chord.DefaultChordModel
import org.locationtech.jts.algorithm.Angle
import org.mkui.geom.*
import org.mkui.visual.Visual
import org.molap.dataframe.DataFrame
import org.molap.dataframe.FilterDataFrame
import org.molap.dataframe.rowmajor.RowMajorDataFrame
import web.svg.SVGCircleElement
import web.svg.SVGGeometryElement
import web.svg.SVGGraphicsElement
import kotlin.math.cos
import kotlin.math.sin

class NodeLOD(val originalShape: SVGGraphicsElement, val dataFrame: DataFrame<Branch, RowMajorDataFrame.Column<Branch, Any?>, Any?>) {
    val filteredDataFrame = FilterDataFrame(dataFrame) {
        (it.from.left7() == it.to.left7())
    }

    val network = QuestDBWeightedNetwork(filteredDataFrame)

    val linksWithExternalConnections = dataFrame.rows().filter { (it.from.left7() != it.to.left7()) }
    val nodesWithExternalConnections = linksWithExternalConnections.map { it.from } + linksWithExternalConnections.map { it.to }

    val nodesVisual = null as Visual<Bus, String>? // SimpleVisual<Bus>()
    val linksVisual = null as Visual<Branch, String>?// SimpleVisual<Branch>()
    val chordModel = DefaultChordModel(network, nodesVisual, linksVisual)

    val geometry = ChordView.Geometry.Curved
    val twisted = false

    val originalBBox = originalShape.getBBox()
    val centerX = originalBBox.x + (originalBBox.width / 2.0)
    val centerY = originalBBox.y + (originalBBox.height / 2.0)
    val screenInnerRadius = originalBBox.width * 0.45
    val screenOuterRadius = originalBBox.width * 0.50

    val template = if(originalShape is SVGCircleElement) ChordTemplate.Circle else ChordTemplate.OriginalShape

    val shapes = HashMap<Bus,HashMap<Bus, Shape>>()

    fun computeOuterPoint(angle: Double): Point2D {
        when(template) {
            ChordTemplate.Circle -> {
                val angleRad: Double = Angle.toRadians(90 - angle)
                val x: Double = screenOuterRadius * cos(angleRad)
                val y: Double = screenOuterRadius * sin(angleRad)
                val screenX: Double = centerX + x
                val screenY: Double = centerY - y
                return Point2D.Double(screenX, screenY)
            }
            ChordTemplate.OriginalShape -> {
                val svgGeometryElement = originalShape as SVGGeometryElement
                val p = svgGeometryElement.getPointAtLength(((angle / 360.0) * svgGeometryElement.getTotalLength()).toFloat())
                return Point2D.Double(p.x, p.y)
            }
        }
    }

    fun computeInnerPoint(angle: Double): Point2D {
        when(template) {
            ChordTemplate.Circle -> {
                val angleRad: Double = Angle.toRadians(90 - angle)
                val x: Double = screenInnerRadius * cos(angleRad)
                val y: Double = screenInnerRadius * sin(angleRad)
                val screenX: Double = centerX + x
                val screenY: Double = centerY - y
                return Point2D.Double(screenX, screenY)
            }
            ChordTemplate.OriginalShape -> {
                val svgGeometryElement = originalShape as SVGGeometryElement
                val p =
                    svgGeometryElement.getPointAtLength(((angle / 360.0) * svgGeometryElement.getTotalLength()).toFloat())
                return Point2D.Double(p.x, p.y)
            }
        }
    }

    fun sectorOfNode(
        template: ChordTemplate,
        centerX: Double,
        centerY: Double,
        screenInnerRadius: Double,
        start: Double,
        extent: Double
    ): PathIterator {
        when(template) {
            ChordTemplate.Circle -> {
                val node1: Arc2D = Arc2D.Double.getArcByCenter(
                    centerX,
                    centerY,
                    screenInnerRadius,
                    90 - start,
                    -extent,
                    Arc2D.OPEN
                )
                return node1.pathIterator
            }
            else -> {
                val p1 = computeInnerPoint(start)
                val p2 = computeInnerPoint(start + extent)
                return Line2D.Double(p1, p2).pathIterator
            }
        }
    }

    fun getShape(from: Bus, to: Bus): Shape? {
        val model = chordModel

        val fromChord: Arc? = model.getArc(from, to)
        if (fromChord != null) {
            var toChord: Arc? = model.getArc(to, from)
            if (toChord == null && fromChord.extent != 0.0) {
//                System.err.println("Error " + from + ", " + to + " " + toChord);
                val chord: Arc? = model.getArc(to)
                if (chord != null) {
                    toChord = Arc(chord.startAngle, 0.0)
                }
            }
            when (geometry) {
                ChordView.Geometry.Straight -> return if (toChord != null) {
                    var shapeMap: MutableMap<Bus, Shape>? = shapes.get(from)
                    val shape: Shape
                    if (shapeMap != null && shapeMap[to] != null) {
                        shape = shapeMap[to]!!
                    } else {
                        val path = GeneralPath()
                        val f1 = computeInnerPoint(fromChord.startAngle)
                        val f2 = computeInnerPoint(fromChord.startAngle + fromChord.extent)
                        val fm = computeInnerPoint(fromChord.startAngle + fromChord.extent / 2)
                        val t1 = computeInnerPoint(toChord.startAngle)
                        val t2 = computeInnerPoint(toChord.startAngle + toChord.extent)
                        val tm = computeInnerPoint(toChord.startAngle + toChord.extent / 2)
                        //                            if (f1.distanceSq(f2) > 0.5 || t1.distanceSq(t2) > 0.5) {
                        if (twisted) {
                            val node1: PathIterator = sectorOfNode(template, centerX, centerY, screenInnerRadius, fromChord.startAngle, fromChord.extent)
                            path.append(node1, false)
                            path.lineTo(t2.x, t2.y)
                            val node2: PathIterator = sectorOfNode(template, centerX, centerY, screenInnerRadius, toChord.startAngle + toChord.extent, -toChord.extent)
                            path.append(node2, true)
                            path.lineTo(f1.x, f1.y)
                        } else {
                            val node1: PathIterator = sectorOfNode(template, centerX, centerY, screenInnerRadius, fromChord.startAngle, fromChord.extent)
                            path.append(node1, false)
                            path.lineTo(t1.x, t1.y)
                            val node2: PathIterator = sectorOfNode(template, centerX, centerY, screenInnerRadius, toChord.startAngle, toChord.extent)
                            path.append(node2, true)
                            path.lineTo(f1.x, f1.y)
                        }
                        path.closePath()
                        shape = path
                        if (shapeMap == null) {
                            shapeMap = HashMap<Bus, Shape>()
                            shapes.put(from, shapeMap)
                        }
                        shapeMap[to] = shape
                        //                            } else {
                        //                                return null;
                        //                            }
                    }
                    shape
                } else {
                    null
                }
                ChordView.Geometry.Curved -> return if (toChord != null) {
                    var shapeMap: MutableMap<Bus, Shape>? = shapes.get(from)
                    var shape: Shape?
                    if (shapeMap != null && shapeMap[to] != null) {
                        shape = shapeMap[to]
                    } else {
                        val path = GeneralPath()
                        val f1 = computeInnerPoint(fromChord.startAngle)
                        val f2 = computeInnerPoint(fromChord.startAngle + fromChord.extent)
                        val fm = computeInnerPoint(fromChord.startAngle + fromChord.extent / 2)
                        val t1 = computeInnerPoint(toChord.startAngle)
                        val t2 = computeInnerPoint(toChord.startAngle + toChord.extent)
                        val tm = computeInnerPoint(toChord.startAngle + toChord.extent / 2)
                        //                            if (f1.distanceSq(f2) > 0.5 || t1.distanceSq(t2) > 0.5) {
                        val controlX = centerX
                        val controlY = centerY
                        val controlPoint: Point2D.Double = Point2D.Double(controlX, controlY)
                        if (twisted) {
                            val curve1: QuadCurve2D = QuadCurve2D.Double(
                                f2.x,
                                f2.y,
                                controlPoint.x,
                                controlPoint.y,
                                t2.x,
                                t2.y
                            )
                            val curve2: QuadCurve2D = QuadCurve2D.Double(
                                t1.x,
                                t1.y,
                                controlPoint.x,
                                controlPoint.y,
                                f1.x,
                                f1.y
                            )
                            val node1: Arc2D = Arc2D.Double.getArcByCenter(
                                centerX,
                                centerY,
                                screenInnerRadius,
                                90 - (fromChord.startAngle + toChord.extent),
                                -fromChord.extent,
                                Arc2D.OPEN
                            )
                            path.append(node1.getPathIterator(), false)
                            path.append(curve1.getPathIterator(), true)
                            val node2: Arc2D = Arc2D.Double.getArcByCenter(
                                centerX,
                                centerY,
                                screenInnerRadius,
                                90 - (toChord.startAngle + toChord.extent),
                                toChord.extent,
                                Arc2D.OPEN
                            )
                            path.append(node2.getPathIterator(), true)
                            path.append(curve2.getPathIterator(), true)
                        } else {
                            val curve1: QuadCurve2D = QuadCurve2D.Double(
                                f2.x,
                                f2.y,
                                controlPoint.x,
                                controlPoint.y,
                                t1.x,
                                t1.y
                            )
                            val curve2: QuadCurve2D = QuadCurve2D.Double(
                                t2.x,
                                t2.y,
                                controlPoint.x,
                                controlPoint.y,
                                f1.x,
                                f1.y
                            )
                            val node1: PathIterator = sectorOfNode(template, centerX, centerY, screenInnerRadius, fromChord.startAngle, fromChord.extent)
                            path.append(node1, false)
                            path.append(curve1.getPathIterator(), true)
                            val node2: PathIterator = sectorOfNode(template, centerX, centerY, screenInnerRadius, toChord.startAngle, toChord.extent)
                            path.append(node2, true)
                            path.append(curve2.getPathIterator(), true)
                        }
                        path.closePath()
                        shape = path
                        if (shapeMap == null) {
                            shapeMap = HashMap<Bus, Shape>()
                            shapes.put(from, shapeMap)
                        }
                        shapeMap[to] = shape

                        //                            } else {
                        //                                return null;
                        //                            }
                        shape = path
                        if (shapeMap == null) {
                            shapeMap = HashMap<Bus, Shape>()
                            shapes.put(from, shapeMap)
                        }
                        shapeMap[to] = shape
                    }
                    shape
                } else {
                    null
                }
            }
        }
        return null
    }
}